import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import React, { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryData, PhoneInputProps as OriginalPhoneInputProps } from 'react-phone-input-2';

import { Container, Label, PhoneInputContainer, StyledInput, StyledPhoneInput } from './PhoneInput.styles';

const MAX_DIGITS = 15;

const updateDidDeleteRef = (
  currentValue: string,
  valueRef: MutableRefObject<string>,
  didDeleteRef: MutableRefObject<boolean>,
) => {
  if (valueRef.current.length > currentValue.length) {
    didDeleteRef.current = true;
  } else if (valueRef.current.length < currentValue.length) {
    didDeleteRef.current = false;
  }
  valueRef.current = currentValue;
};

const triggerInputEvent = (inputRef: React.RefObject<HTMLInputElement>, value: string) => {
  if (!inputRef.current) return;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
  if (!nativeInputValueSetter) return;

  nativeInputValueSetter.call(inputRef.current, value);
  inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
};

const extractDigits = (text: string) =>
  text.split('').reduce((accumulator, letter) => {
    if (letter >= '0' && letter <= '9') return accumulator + letter;
    return accumulator;
  }, '');

const extractDots = (text: string) => (text || '').replace(/[^.]/g, '');

const removePlusPrefix = (text: string) => {
  const safe = text || '';
  return safe
    .split('+')
    .slice(safe.trim().startsWith('+') ? 1 : 0)
    .join('+');
};

type PhoneInputProps = Omit<OriginalPhoneInputProps, 'onChange'> & {
  onChange?: (phone: string, isComplete: boolean) => void;
  error?: string;
  noMargin?: boolean;
  required?: boolean;
  defaultValue?: string;
  hidePlaceholder?: boolean;

  /**
   * Your styles file expects `$requiredLabel?: string`
   * so we accept boolean or string and normalize before passing to <Label />.
   */
  $requiredLabel?: boolean | string;
};

enum InputField {
  CountryCode = 'CountryCode',
  PhoneNumber = 'PhoneNumber',
}

const PhoneInput: React.FC<PhoneInputProps> = (props) => {
  const { t } = useTranslation();

  const initialValueRef = useRef(props.value);

  const [fullPhoneNumber, setFullPhoneNumber] = useState(props.value || '');
  const [countryCode, setCountryCode] = useState('+1');
  const countryCodeRef = useRef(countryCode);

  const [countryISOCode, setCountryISOCode] = useState('us');

  const [phoneNumber, setPhoneNumber] = useState(props.defaultValue || '');
  const phoneNumberRef = useRef(phoneNumber);

  const didDeletePhoneNumberRef = useRef(false);
  const didDeleteCountryCodeRef = useRef(false);

  const hiddenPhoneInputRef = useRef<HTMLInputElement | null>(null);
  const phoneNumberInputRef = useRef<HTMLInputElement | null>(null);
  const countryCodeInputRef = useRef<HTMLInputElement | null>(null);

  const [lastInputField, setLastInputField] = useState<InputField>();

  // Track deletes/insertions for both fields
  updateDidDeleteRef(phoneNumber, phoneNumberRef, didDeletePhoneNumberRef);
  updateDidDeleteRef(countryCode, countryCodeRef, didDeleteCountryCodeRef);

  const isTab = (event: ReactKeyboardEvent<HTMLInputElement>) => event.key === 'Tab';

  // Keep initialValueRef synced if parent changes value (safe even if not needed)
  useEffect(() => {
    initialValueRef.current = props.value;
  }, [props.value]);

  useEffect(() => {
    const newFullPhoneNumber = `${countryCode} ${phoneNumber}`.trim();
    const onlyDigits = extractDigits(newFullPhoneNumber);

    if (onlyDigits.length <= MAX_DIGITS) {
      setFullPhoneNumber(newFullPhoneNumber);
    } else {
      // avoid stale `fullPhoneNumber` usage by using the computed value
      setPhoneNumber((newFullPhoneNumber.split(countryCode).pop() || '').trim());
    }
  }, [countryCode, phoneNumber]);

  useEffect(() => triggerInputEvent(hiddenPhoneInputRef, fullPhoneNumber), [fullPhoneNumber]);

  const onHiddenPhoneInputChange = (
    _value: string,
    data: CountryData,
    _event: React.ChangeEvent<HTMLInputElement>,
    formattedValue: string,
  ) => {
    const countryCodeWithoutPlus = removePlusPrefix(countryCode);

    let processPhoneInfo = !!data.dialCode && countryCodeWithoutPlus.startsWith(data.dialCode);
    let finalCountryCode = countryCode;
    let finalPhoneNumber = phoneNumber;

    // Parse initial render value using formattedValue if it matches dialCode
    if (
      data.dialCode &&
      removePlusPrefix(formattedValue).startsWith(data.dialCode) &&
      initialValueRef.current === formattedValue &&
      finalCountryCode === '+1' &&
      !finalPhoneNumber
    ) {
      processPhoneInfo = true;
      const phoneParts = formattedValue.split(' ');
      finalCountryCode = phoneParts[0];
      finalPhoneNumber = phoneParts.slice(1).join(' ');
    }

    if (processPhoneInfo) {
      if (!didDeleteCountryCodeRef.current) {
        setCountryISOCode(data.countryCode);
        finalCountryCode = `+${data.dialCode}`;

        setCountryCode(finalCountryCode);

        // remove country code from formatted value
        finalPhoneNumber = (formattedValue.split(finalCountryCode)[1] || '').trim();

        if (
          (finalPhoneNumber !== phoneNumber &&
            (!didDeletePhoneNumberRef.current || lastInputField === InputField.CountryCode)) ||
          !finalPhoneNumber
        ) {
          setPhoneNumber(finalPhoneNumber);
        }
      }
    }

    // Safari focus fix
    if (lastInputField) {
      if (
        phoneNumber === finalPhoneNumber &&
        (lastInputField === InputField.CountryCode || (!phoneNumber.length && didDeletePhoneNumberRef.current)) &&
        (!data.dialCode || countryCodeWithoutPlus !== data.dialCode)
      ) {
        countryCodeInputRef.current?.focus();
      } else {
        phoneNumberInputRef.current?.focus();
      }
    }

    finalPhoneNumber = finalPhoneNumber.trim();

    if (!finalPhoneNumber) {
      props.onChange?.('', false);
      return;
    }

    finalCountryCode = finalCountryCode.trim();

    let fullNumber = '';
    if (finalCountryCode) {
      fullNumber += `${finalCountryCode.startsWith('+') ? finalCountryCode : `+${finalCountryCode}`} `;
    }
    fullNumber += finalPhoneNumber;
    fullNumber = fullNumber.trim();

    const isUnknownFormat = extractDots(data.format).length > MAX_DIGITS;
    const isComplete = isUnknownFormat || (processPhoneInfo && data.format.length === fullNumber.length);

    props.onChange?.(fullNumber, isComplete);
  };

  // Normalize $requiredLabel to what your styles file expects (string | undefined)
  const requiredLabelProp: string | undefined =
    typeof props.$requiredLabel === 'string' ? props.$requiredLabel : props.$requiredLabel ? 'true' : undefined;

  return (
    <Container $noMargin={props.noMargin || false}>
      <Label $required={props.required} $requiredLabel={requiredLabelProp}>
        {t('auth.signUp.phoneNumber')}
      </Label>

      {/* PhoneInputContainer typing doesn't accept `error` in your current inferred types.
          Without changing styles, this is the safest local fix. */}
      <PhoneInputContainer {...({ error: props.error } as any)}>
        {/* StyledPhoneInput doesn't accept `style`, so hide it with a wrapper */}
        <div style={{ display: 'none' }}>
          <StyledPhoneInput inputProps={{ ref: hiddenPhoneInputRef }} onChange={onHiddenPhoneInputChange} />
        </div>

        <StyledPhoneInput
          {...props}
          inputProps={{ ref: countryCodeInputRef }}
          key={countryISOCode}
          country={countryISOCode}
          value={countryCode}
          onChange={(value: string, data: CountryData) => {
            setCountryCode(value);
            setCountryISOCode(data.countryCode);
          }}
          placeholder="+1"
          onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
            setLastInputField(InputField.CountryCode);
            if (isTab(e) && !e.shiftKey) {
              phoneNumberInputRef.current?.focus();
              e.preventDefault();
            }
          }}
        />

        <StyledInput
          $noMargin={props.noMargin || false}
          ref={phoneNumberInputRef}
          value={phoneNumber}
          aria-label={t('auth.signUp.phoneNumber')}
          required={props.required}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value.trim())}
          fullWidth={false}
          onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
            setLastInputField(InputField.PhoneNumber);

            if (e.key === 'Backspace' && !phoneNumber) {
              countryCodeInputRef.current?.focus();
              e.preventDefault();
            } else if (isTab(e) && e.shiftKey) {
              countryCodeInputRef.current?.focus();
              e.preventDefault();
            }
          }}
          error={props.error}
          placeholder={!props.hidePlaceholder ? t('auth.signUp.phoneNumber') : undefined}
        />
      </PhoneInputContainer>
    </Container>
  );
};

export default PhoneInput;
